import type { JSX } from "preact";
import type { ConversationRow, InboxCounts } from "../../api";
import { Avatar } from "../../components/Avatar";
import { CheckIcon, SearchIcon } from "../../components/Icons";
import { ago, agoPhrase, visitorLabel, waitHeat } from "../../lib/format";
import { useI18n, type Dict } from "../../lib/i18n";

export type InboxTab = "mine" | "unassigned" | "active" | "pending" | "resolved";

const TABS: Array<{ key: InboxTab; label: (t: Dict) => string; count?: keyof InboxCounts }> = [
  { key: "mine", label: (t) => t.inbox.tabMine, count: "mine" },
  { key: "unassigned", label: (t) => t.inbox.tabUnassigned, count: "unassigned" },
  { key: "active", label: (t) => t.inbox.tabActive, count: "active" },
  { key: "pending", label: (t) => t.inbox.tabPending, count: "pending" },
  { key: "resolved", label: (t) => t.inbox.tabResolved },
];

/** How many conversations the inbox asks for at first, and adds with each "show more". */
export const INBOX_PAGE = 50;
/** The server's ceiling for one list request. */
export const INBOX_MAX = 300;

/** What a tab asks the server for. */
export function tabQuery(tab: InboxTab): { status: string; assigned: string } {
  switch (tab) {
    case "mine":
      return { status: "active", assigned: "me" };
    case "unassigned":
      return { status: "active", assigned: "unassigned" };
    case "pending":
      return { status: "pending", assigned: "any" };
    case "resolved":
      return { status: "resolved", assigned: "any" };
    default:
      return { status: "active", assigned: "any" };
  }
}

/**
 * Whether to offer "show more": the list came back full, and asking for a
 * longer one is still allowed. The live inbox is ordered by activity, which
 * cannot be paged by id, so more is asked for as a longer first page.
 */
export function canShowMore(rowCount: number, limit: number): boolean {
  return rowCount >= limit && limit < INBOX_MAX;
}

export function previewText(row: ConversationRow, t: Dict): string {
  const p = row.preview;
  if (!p) return t.inbox.noMessages;
  const text = p.text || (p.hasImage ? t.inbox.sentImage : p.hasFile ? t.inbox.sentFile : "");
  return p.kind === "visitor" ? text : t.inbox.teamPrefix(text);
}

/** One conversation in a list — shared by the inbox and the history. */
export function ConversationRowButton({
  row,
  selected,
  onSelect,
  now,
  meta,
  picking,
}: {
  row: ConversationRow;
  selected: boolean;
  onSelect: () => void;
  now: number;
  /** Replaces the relative time on the right, e.g. a closing date in the history. */
  meta?: string;
  /** Set while rows are being ticked for a bulk action: a click ticks instead of opening. */
  picking?: { picked: boolean };
}): JSX.Element {
  const { t, lang } = useI18n();
  const heat = waitHeat(row.waitingSince, now);
  const name = visitorLabel(row, lang);
  return (
    <button
      class={`row${row.unread > 0 ? " unread" : ""}${picking?.picked ? " picked" : ""}`}
      aria-selected={picking ? undefined : selected}
      aria-pressed={picking ? picking.picked : undefined}
      onClick={onSelect}
    >
      {heat !== "none" ? <span class={`wait ${heat}`} title={t.inbox.waitingTitle(agoPhrase(row.waitingSince!, now, lang))} /> : null}
      {picking ? (
        <span class={`pick${picking.picked ? " on" : ""}`} aria-hidden="true">
          {picking.picked ? <CheckIcon size={16} /> : null}
        </span>
      ) : (
        <span class="avatar visitor" style={{ width: "36px", height: "36px", fontSize: "14px" }} aria-hidden="true">
          {name.charAt(0).toLocaleUpperCase(lang)}
        </span>
      )}
      <span style={{ minWidth: 0 }}>
        <span class="line1">
          <span class="who">{name}</span>
          {row.visitorVerifiedAt ? (
            <span class="chip verified" title={t.inbox.verifiedTitle}>
              ✓
            </span>
          ) : null}
          <span class={`when${heat === "warm" || heat === "hot" ? ` ${heat}` : ""}`}>
            {meta ?? ago(row.waitingSince ?? row.lastMessageAt, now, lang)}
          </span>
        </span>
        <span class="preview" style={{ display: "block" }}>
          {row.snippet ?? previewText(row, t)}
        </span>
        <span class="line3">
          <span class="site">{row.siteName}</span>
          {row.tags.slice(0, 2).map((tag) => (
            <span key={tag} class="chip">
              {tag}
            </span>
          ))}
          <span class="end">
            {row.rating ? (
              <span class="stars" aria-label={t.context.stars(row.rating)}>
                {"★".repeat(row.rating)}
              </span>
            ) : null}
            {row.assignee ? (
              <span title={t.inbox.assigneeTitle(row.assignee.displayName)}>
                <Avatar name={row.assignee.displayName} color={row.assignee.color} url={row.assignee.avatarUrl} size={18} />
              </span>
            ) : null}
            {row.unread > 0 ? (
              <span class="badge" aria-label={t.inbox.unread(row.unread)}>
                {row.unread}
              </span>
            ) : null}
          </span>
        </span>
      </span>
    </button>
  );
}

export type BulkAction = "resolve" | "assign" | "delete";

export function ConversationList({
  tab,
  onTab,
  counts,
  query,
  onQuery,
  sites,
  siteId,
  onSite,
  tag,
  onTag,
  rows,
  loading,
  selectedId,
  onSelect,
  now,
  hasMore,
  onMore,
  picked,
  onPicking,
  onPick,
  onPickAll,
  onBulk,
  bulkBusy,
  isAdmin,
}: {
  tab: InboxTab;
  onTab: (tab: InboxTab) => void;
  counts: InboxCounts | null;
  query: string;
  onQuery: (q: string) => void;
  /** The sites this operator serves; the picker only shows when there is more than one. */
  sites: Array<{ id: number; name: string }>;
  siteId: number | null;
  onSite: (id: number | null) => void;
  tag: string;
  onTag: (tag: string) => void;
  rows: ConversationRow[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
  now: number;
  hasMore: boolean;
  onMore: () => void;
  /** Null while not selecting; otherwise the ticked conversation ids. */
  picked: ReadonlySet<number> | null;
  onPicking: (on: boolean) => void;
  onPick: (id: number) => void;
  onPickAll: () => void;
  onBulk: (action: BulkAction) => void;
  bulkBusy: boolean;
  isAdmin: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const filtered = query || tag || siteId !== null;
  return (
    <section class="pane list-pane" aria-label={t.inbox.listLabel}>
      <div class="list-head">
        <div class="list-title">
          <h1>{t.inbox.title}</h1>
          <button class="btn ghost sm" aria-pressed={picked !== null} onClick={() => onPicking(picked === null)}>
            {picked === null ? t.inbox.select : t.inbox.selectDone}
          </button>
        </div>
        <div class="tabs" role="tablist" aria-label={t.inbox.filterLabel}>
          {TABS.map((item) => (
            <button key={item.key} role="tab" aria-selected={tab === item.key} onClick={() => onTab(item.key)}>
              {item.label(t)}
              {item.count && counts ? <span class="n">{counts[item.count]}</span> : null}
            </button>
          ))}
        </div>
        <div class="search">
          <SearchIcon />
          <input
            type="search"
            placeholder={t.inbox.searchPlaceholder}
            aria-label={t.inbox.searchLabel}
            value={query}
            onInput={(e) => onQuery(e.currentTarget.value)}
          />
        </div>
        <div class="list-filters">
          {sites.length > 1 ? (
            <select
              aria-label={t.common.site}
              value={siteId ?? ""}
              onChange={(e) => onSite(e.currentTarget.value ? Number(e.currentTarget.value) : null)}
            >
              <option value="">{t.common.allSites}</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          ) : null}
          <input
            type="text"
            placeholder={t.inbox.filterTag}
            aria-label={t.inbox.filterTag}
            maxLength={40}
            value={tag}
            onInput={(e) => onTag(e.currentTarget.value)}
          />
        </div>
      </div>

      {picked !== null ? (
        <div class="bulk-bar" role="group" aria-label={t.inbox.selected(picked.size)}>
          <span class="bulk-count">{t.inbox.selected(picked.size)}</span>
          <button class="btn ghost sm" onClick={onPickAll} disabled={rows.length === 0}>
            {t.inbox.selectAll}
          </button>
          <span class="bulk-actions">
            <button class="btn sm" disabled={picked.size === 0 || bulkBusy} onClick={() => onBulk("resolve")}>
              {t.inbox.bulkResolve}
            </button>
            <button class="btn sm" disabled={picked.size === 0 || bulkBusy} onClick={() => onBulk("assign")}>
              {t.inbox.bulkAssignMe}
            </button>
            {isAdmin ? (
              <button class="btn sm danger" disabled={picked.size === 0 || bulkBusy} onClick={() => onBulk("delete")}>
                {t.inbox.bulkDelete}
              </button>
            ) : null}
          </span>
        </div>
      ) : null}

      <div class="rows">
        {rows.length === 0 ? (
          <div class="empty">
            {loading ? (
              t.common.loading
            ) : filtered ? (
              <div>
                <strong>{t.inbox.noResultsTitle}</strong>
                {t.inbox.noResultsHint}
              </div>
            ) : (
              <div>
                <strong>{t.inbox.emptyTitle}</strong>
                {t.inbox.emptyHint}
              </div>
            )}
          </div>
        ) : null}

        {rows.map((row) => (
          <ConversationRowButton
            key={row.id}
            row={row}
            selected={row.id === selectedId}
            onSelect={() => (picked !== null ? onPick(row.id) : onSelect(row.id))}
            now={now}
            picking={picked !== null ? { picked: picked.has(row.id) } : undefined}
          />
        ))}

        {hasMore ? (
          <div class="load-more">
            <button class="btn sm" disabled={loading} onClick={onMore}>
              {loading ? t.common.loading : t.inbox.loadMore}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
